import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import invoiceService from '../features/invoice/invoiceService';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonthStr = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const startOfWeekStr = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Dom
  const diff = (day + 6) % 7; // lunes como inicio
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const lastMonthRange = () => {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const last = new Date(d.getFullYear(), d.getMonth(), 0);
  return [first.toISOString().slice(0, 10), last.toISOString().slice(0, 10)];
};

const number = (v) => (typeof v === 'number' ? v : Number(v || 0));
const fmt = (n) => n?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? '0,00';

export default function IvaSalesReport() {
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const initialFrom = params.get('from') || firstDayOfMonthStr();
  const initialTo = params.get('to') || todayStr();
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { range, count, rows, totals }
  // Filtros
  const [letra, setLetra] = useState('Todas'); // Todas | A | B | C | M
  const [clase, setClase] = useState('Todos'); // Todos | Factura | NC | ND
  const [docFilter, setDocFilter] = useState('');

  const canQuery = useMemo(() => !!from && !!to, [from, to]);
  const [sort, setSort] = useState({ key: 'fecha', dir: 'desc' });

  const handleQuery = async () => {
    if (!canQuery) return;
    try {
      setLoading(true);
      const resp = await invoiceService.getIvaSalesReport({ from, to });
      setData(resp);
      if (resp?.count === 0) toast.info('No hay comprobantes en el rango seleccionado');
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || 'Error consultando reporte IVA';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const resp = await invoiceService.downloadIvaSalesReport({ from, to });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      // Nombre sugerido desde backend está en Content-Disposition
      const cd = resp.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `iva_ventas_${from}_a_${to}.xlsx`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err?.message || 'Error exportando XLSX';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const totals = data?.totals || { neto: 0, iva: 0, total: 0, alicuotas: [] };
  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let va = a[k], vb = b[k];
      if (k === 'fecha') { va = new Date(a.fecha).getTime(); vb = new Date(b.fecha).getTime(); }
      if (typeof va === 'string') va = va.toString();
      if (typeof vb === 'string') vb = vb.toString();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, sort]);

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: (s.dir === 'asc' ? 'desc' : 'asc') } : { key, dir: 'asc' });
  };

  // Mapeos auxiliares para filtros
  const letraByTipo = (t) => {
    const n = Number(t);
    if ([1, 2, 3].includes(n)) return 'A';
    if ([6, 7, 8].includes(n)) return 'B';
    if ([11, 12, 13].includes(n)) return 'C';
    if ([51, 52, 53].includes(n)) return 'M';
    return 'Otro';
  };
  const claseByTipo = (t) => {
    const n = Number(t);
    if ([1, 6, 11, 51].includes(n)) return 'Factura';
    if ([3, 8, 13, 53].includes(n)) return 'NC';
    if ([2, 7, 12, 52].includes(n)) return 'ND';
    return 'Otro';
  };
  const filteredRows = useMemo(() => {
    return sortedRows.filter(r => {
      if (letra !== 'Todas' && letraByTipo(r.tipoComprobante) !== letra) return false;
      if (clase !== 'Todos' && claseByTipo(r.tipoComprobante) !== clase) return false;
      if (docFilter && !String(r.docNro || '').includes(docFilter.trim())) return false;
      return true;
    });
  }, [sortedRows, letra, clase, docFilter]);

  return (
    <div style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>📑 Reporte IVA Ventas</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={btnNav} onClick={() => window.location.assign('/')}>🏠 Dashboard</button>
          <button style={btnNav} onClick={() => window.location.assign('/sales')}>💵 Punto de Venta</button>
          <button style={{ ...btnNav, background: '#6c757d' }} onClick={() => window.location.assign('/reports')}>📊 Reportes</button>
        </div>
      </div>

      <div style={sectionBox}>
        <p style={{ color: '#666', marginTop: 0 }}>Disponible para emisores Responsable Inscripto. Rango por fecha de comprobante.</p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <label>Desde
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={styles.input} />
          </label>
          <label>Hasta
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={styles.input} />
          </label>
          <button onClick={handleQuery} disabled={!canQuery || loading} style={styles.btnPrimary}>Consultar</button>
          <button onClick={handleExport} disabled={!canQuery || loading} style={styles.btnSecondary}>Exportar Excel</button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btnLight} onClick={() => { setFrom(todayStr()); setTo(todayStr()); }}>Hoy</button>
            <button style={btnLight} onClick={() => { setFrom(startOfWeekStr()); setTo(todayStr()); }}>Esta semana</button>
            <button style={btnLight} onClick={() => { setFrom(firstDayOfMonthStr()); setTo(todayStr()); }}>Este mes</button>
            <button style={btnLight} onClick={() => { const [f, t] = lastMonthRange(); setFrom(f); setTo(t); }}>Mes anterior</button>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label>Letra
              <select value={letra} onChange={(e) => setLetra(e.target.value)} style={styles.input}>
                <option>Todas</option>
                <option>A</option>
                <option>B</option>
                <option>C</option>
                <option>M</option>
              </select>
            </label>
            <label>Clase
              <select value={clase} onChange={(e) => setClase(e.target.value)} style={styles.input}>
                <option>Todos</option>
                <option>Factura</option>
                <option>NC</option>
                <option>ND</option>
              </select>
            </label>
            <label>DocNro
              <input placeholder="Buscar doc..." value={docFilter} onChange={(e) => setDocFilter(e.target.value)} style={styles.input} />
            </label>
          </div>
        </div>
      </div>

      {loading && <div style={{ marginTop: 12 }}>Cargando...</div>}

      {data && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <strong>Resultados:</strong> {filteredRows.length} de {data.count} comprobantes | Neto: ${fmt(number(totals.neto))} | IVA: ${fmt(number(totals.iva))} | Total: ${fmt(number(totals.total))}
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '65vh' }}>
            <table style={tableStyle}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ backgroundColor: '#007bff', color: '#fff' }}>
                  {headerCells.map((h) => (
                    <th key={h.label} style={thStyle} onClick={h.sortKey ? () => toggleSort(h.sortKey) : undefined}>
                      {h.label}{h.sortKey && sort.key === h.sortKey ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>{String(r.fecha).slice(0, 10)}</td>
                    <td style={tdStyle}>{r.tipoComprobante} {r.tipoComprobanteDescripcion}</td>
                    <td style={tdStyle}>{String(r.puntoVenta).padStart(5, '0')}</td>
                    <td style={tdStyle}>{String(r.numero).padStart(8, '0')}</td>
                    <td style={tdStyle}>{r.docTipo}</td>
                    <td style={tdStyle}>{r.docNro}</td>
                    <td style={tdRight}>${fmt(number(r.neto))}</td>
                    <td style={{ ...tdRight, color: number(r.ivaImporte) < 0 ? '#c92a2a' : undefined }}> ${fmt(number(r.ivaImporte))}</td>
                    <td style={{ ...tdRight, color: number(r.total) < 0 ? '#c92a2a' : undefined }}> ${fmt(number(r.total))}</td>
                    <td style={tdRight}>${fmt(number(r.iva_10_5_base))}</td>
                    <td style={tdRight}>${fmt(number(r.iva_10_5_importe))}</td>
                    <td style={tdRight}>${fmt(number(r.iva_21_base))}</td>
                    <td style={tdRight}>${fmt(number(r.iva_21_importe))}</td>
                    <td style={tdStyle}>
                      <button style={btnLight} onClick={async () => {
                        if (!r?.id) {
                          toast.error('No hay ID de factura disponible para esta fila. Intente consultar sin filtros o use "Consultar" nuevamente.');
                          return;
                        }
                        try {
                          setLoading(true);
                          const resp = await invoiceService.downloadInvoicePdf(r.id);
                          const blob = new Blob([resp.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          {
                            const tipo = Number(r.tipoComprobante);
                            const isNC = [3, 8, 13, 53].includes(tipo);
                            a.download = `${isNC ? 'nota_credito' : 'factura'}_${String(r.numero).padStart(8, '0')}.pdf`;
                          }
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          console.error(e);
                          const serverMsg = e?.response?.data?.message || e?.message || 'No se pudo descargar el PDF';
                          toast.error(serverMsg);
                        } finally { setLoading(false); }
                      }} title={r?.id ? 'Descargar PDF' : 'ID de factura ausente'}>PDF</button>
                    </td>
                  </tr>
                ))}
                <tr style={{ background: '#fafafa', fontWeight: 'bold' }}>
                  <td style={tdStyle} colSpan={6}>Totales</td>
                  <td style={tdRight}>${fmt(number(totals.neto))}</td>
                  <td style={{ ...tdRight, color: number(totals.iva) < 0 ? '#c92a2a' : undefined }}>${fmt(number(totals.iva))}</td>
                  <td style={{ ...tdRight, color: number(totals.total) < 0 ? '#c92a2a' : undefined }}>${fmt(number(totals.total))}</td>
                  <td style={tdRight}>${fmt(number(sumCol(filteredRows, 'iva_10_5_base')))}</td>
                  <td style={tdRight}>${fmt(number(sumCol(filteredRows, 'iva_10_5_importe')))}</td>
                  <td style={tdRight}>${fmt(number(sumCol(filteredRows, 'iva_21_base')))}</td>
                  <td style={tdRight}>${fmt(number(sumCol(filteredRows, 'iva_21_importe')))}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const headerCells = [
  { label: 'Fecha', sortKey: 'fecha' },
  { label: 'Tipo' },
  { label: 'PV' },
  { label: 'Número', sortKey: 'numero' },
  { label: 'DocTipo' },
  { label: 'DocNro', sortKey: 'docNro' },
  { label: 'Neto', sortKey: 'neto' },
  { label: 'IVA', sortKey: 'ivaImporte' },
  { label: 'Total', sortKey: 'total' },
  { label: 'IVA 10.5 Base', sortKey: 'iva_10_5_base' },
  { label: 'IVA 10.5 Importe', sortKey: 'iva_10_5_importe' },
  { label: 'IVA 21 Base', sortKey: 'iva_21_base' },
  { label: 'IVA 21 Importe', sortKey: 'iva_21_importe' },
  { label: 'PDF' },
];

const sumCol = (rows, key) => (Array.isArray(rows) ? rows.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0) : 0);

const styles = {
  input: { marginLeft: 6, padding: '6px 8px' },
  btnPrimary: { background: '#007bff', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' },
  btnSecondary: { background: '#28a745', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' },
  th: { textAlign: 'left', padding: 10, borderBottom: '2px solid #e9ecef', fontSize: 13 },
  td: { padding: 10, fontSize: 13 },
  tdRight: { padding: 10, fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' },
};

// Estilos reutilizados tipo Products
const btnNav = { background: '#007bff', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const sectionBox = { backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' };
const thStyle = { padding: '10px', borderBottom: '2px solid #ccc', textAlign: 'left', cursor: 'pointer' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #eee' };
const tdRight = { ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' };

const btnLight = { background: '#f1f3f5', color: '#333', border: '1px solid #dee2e6', padding: '6px 8px', borderRadius: '6px', cursor: 'pointer' };
